import { ref } from 'vue'

import { stringToColor, textColor } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

/**
 * Defaults for a brand new budget document row, shared by every "add row" entry point.
 * A comment row is only a comment: it carries no pricing at all, beyond the price the
 * model requires.
 */
export const useNewBudgetDocumentRow = () => {
  const accountingStore = useAccountingStore()
  const webSiteStore = useWebSiteStore()

  const newBudgetDocumentRow = ({ budgetDocumentId, listingId, currencyRef, documentRowType }) => {
    const row = {
      budget_document: budgetDocumentId,
      listing: listingId,
      currency_ref: currencyRef,
      document_row_type: documentRowType,
      price: 0,
      tags: [],
      row_files: [],
      row_urls: [],
    }

    if (documentRowType === webSiteStore.documentRowTypeEnum.COMMENT) {
      return row
    }

    return {
      ...row,
      quantity: 1,
      cost: 0,
      cost_valorization: accountingStore.costValorizationsEnum.PRICE,
      unit_of_measure_ref: documentRowType === webSiteStore.documentRowTypeEnum.EXTRA
        ? webSiteStore.getDefaultExtraUnitOfMeasure()?.id
        : webSiteStore.getDefaultUnitOfMeasure()?.id,
      vat_type_ref: webSiteStore.getDefaultVatType()?.id,
      vat_type_percentage: webSiteStore.getDefaultVatType()?.percentage || 0,
    }
  }

  return { newBudgetDocumentRow }
}

export const useBudgetDocumentRowLabels = ({ showListingFirst, groupRowsBy = ref(null), hasListing }) => {
  const accountingStore = useAccountingStore()
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()

  // Rows may reference items of any listing, while planningStore.listingItems can be narrowed to one.
  const listingItemsById = ref({})

  const loadListingItemsAsync = async() => {
    const list = (await planningStore.fetchListingItemsAsync({ all: true })) ?? []
    listingItemsById.value = Object.fromEntries(list.map(li => [li.id, li]))
  }

  const getRowListingItem = (rowItem) =>
    listingItemsById.value[rowItem.listing_item] || planningStore.listingItems.find(li => li.id === rowItem.listing_item)

  const getRowListingText = (rowItem) => rowItem.listing_item_text || getRowListingItem(rowItem)?.name

  const getRowServiceName = (rowItem) => {
    const service = planningStore.getServices().find(s => s.id === getRowListingItem(rowItem)?.service)
    return service ? planningStore.getServiceName(service) : null
  }

  const getRowItemLabelSuffix = (rowItem) => {
    if (rowItem.document_row_type !== webSiteStore.documentRowTypeEnum.LISTING_ITEM) return null
    const listingText = getRowListingText(rowItem)
    const serviceName = getRowServiceName(rowItem)
    if (!listingText || !serviceName || listingText === serviceName) return null
    return showListingFirst.value ? serviceName : listingText
  }

  const getRowItemLabel = (rowItem) => {
    if (rowItem.document_row_type === webSiteStore.documentRowTypeEnum.LISTING_ITEM) {
      const listingItem = getRowListingItem(rowItem)
      const listingText = getRowListingText(rowItem)
      const serviceName = getRowServiceName(rowItem)
      const name = showListingFirst.value ? (listingText || serviceName) : (serviceName || listingText)
      return hasListing(rowItem) ? name : `${name} - ${listingItem?.listing_name}`
    }
    if (rowItem.document_row_type === webSiteStore.documentRowTypeEnum.EXTRA) {
      const category = accountingStore.getLocalizedBudgetDocumentRowCategories().find(c => c.id === rowItem.budget_document_row_category_ref)
      return category?.name
    }
    return rowItem.description || rowItem.notes
  }

  const getRowItemUOM = (rowItem) => webSiteStore.getLocalizedUnitsOfMeasure().find(u => u.id === rowItem.unit_of_measure_ref)

  const getRowItemLabelDetail = (rowItem) => {
    if (rowItem.document_row_type === webSiteStore.documentRowTypeEnum.LISTING_ITEM) {
      return ` x ${rowItem.quantity} ${getRowItemUOM(rowItem)?.name}`
    }
    if (rowItem.document_row_type === webSiteStore.documentRowTypeEnum.EXTRA) {
      return ` - ${rowItem.quantity} ${getRowItemUOM(rowItem)?.name}`
    }
    return ''
  }

  const getTagsChips = (rowItem) => {
    const getName = (tagId) => accountingStore.getLocalizedBudgetTags().sort((a, b) => a.name.localeCompare(b.name)).find(t => t.id === tagId)?.name || tagId

    const currentRowType = webSiteStore.getLocalizedDocumentRowTypes().find(t => t.id === rowItem.document_row_type)

    const typeChip = {
      id: 'type',
      name: currentRowType.name,
      color: rowItem.document_row_type === webSiteStore.documentRowTypeEnum.EXTRA ? 'positive' : rowItem.document_row_type === webSiteStore.documentRowTypeEnum.COMMENT ? 'info' : 'grey',
    }

    const tagsChips = groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG ? [] : (rowItem.tags || []).map(tag => ({
      id: tag,
      name: getName(tag),
      color: stringToColor(getName(tag)),
    }))

    if (rowItem.document_row_type !== webSiteStore.documentRowTypeEnum.LISTING_ITEM) {
      tagsChips.unshift(typeChip)
    }

    // Service-first mode: the listing name becomes a chip, as in the timeline's service list.
    const listingChipName = !showListingFirst.value ? getRowItemLabelSuffix(rowItem) : null
    if (listingChipName) {
      const color = stringToColor(listingChipName)
      tagsChips.push({ id: `listing-${listingChipName}`, name: listingChipName, color, textColor: textColor(color) })
    }

    return tagsChips
  }

  return { loadListingItemsAsync, getRowItemLabel, getRowItemLabelDetail, getRowItemLabelSuffix, getTagsChips }
}
